import moment from "moment-timezone";
import React from "react";
import {
  COACH_APP_APP_STORE_URL,
  COACH_APP_PLAY_STORE_URL,
} from "../../../constants";
import FloatingButtons from "../../../parts/FloatingButtons";
import Toast from "../../../parts/toast";
import { useRillContext } from "../../../rill/RillReact/share/base";
import { SettingsSkeleton } from "./SchedulingSkeletons";
import { normalizeBookingLimitPeriodForApi } from "./scheduling-limit-period";

const HORIZON_OPTIONS = [
  { value: "2", label: "2 weeks" },
  { value: "4", label: "4 weeks" },
  { value: "8", label: "8 weeks" },
  { value: "12", label: "12 weeks" },
];

const TIME_PERIOD_OPTIONS = [
  { id: "daily", label: "Daily" },
  { id: "weekly", label: "Weekly" },
  { id: "monthly", label: "Monthly" },
];

const MAX_SESSIONS_OPTIONS = Array.from({ length: 30 }, (_, i) => i + 1);

const TIMEZONE_OPTIONS = [
  { value: "America/New_York", label: "America/New York" },
  { value: "America/Los_Angeles", label: "America/Los Angeles" },
  { value: "America/Chicago", label: "America/Chicago" },
  { value: "America/Denver", label: "America/Denver" },
  { value: "America/Toronto", label: "America/Toronto" },
  { value: "America/Vancouver", label: "America/Vancouver" },
  { value: "America/Mexico_City", label: "America/Mexico City" },
  { value: "America/Sao_Paulo", label: "America/São Paulo" },
  { value: "Europe/London", label: "Europe/London" },
  { value: "Europe/Paris", label: "Europe/Paris" },
  { value: "Europe/Berlin", label: "Europe/Berlin" },
  { value: "Europe/Amsterdam", label: "Europe/Amsterdam" },
  { value: "Europe/Madrid", label: "Europe/Madrid" },
  { value: "Europe/Rome", label: "Europe/Rome" },
  { value: "Europe/Istanbul", label: "Europe/Istanbul" },
  { value: "Asia/Dubai", label: "Asia/Dubai" },
  { value: "Asia/Kolkata", label: "Asia/Kolkata" },
  { value: "Asia/Shanghai", label: "Asia/Shanghai" },
  { value: "Asia/Tokyo", label: "Asia/Tokyo" },
  { value: "Asia/Singapore", label: "Asia/Singapore" },
  { value: "Australia/Sydney", label: "Australia/Sydney" },
  { value: "Australia/Melbourne", label: "Australia/Melbourne" },
  { value: "Pacific/Auckland", label: "Pacific/Auckland" },
  { value: "UTC", label: "UTC" },
];

/**
 * "GMT-6 • 1:07 p.m" - a zone's UTC offset and its current wall-clock time.
 */
function formatZoneOffsetAndTime(zone, nowMs) {
  const m = moment(nowMs).tz(zone);
  const offsetMin = m.utcOffset();
  const sign = offsetMin < 0 ? "-" : "+";
  const absMin = Math.abs(offsetMin);
  const offHours = Math.floor(absMin / 60);
  const offMins = absMin % 60;
  const offset = `GMT${sign}${offHours}${
    offMins ? `:${String(offMins).padStart(2, "0")}` : ""
  }`;
  const hour24 = m.hour();
  const minute = String(m.minute()).padStart(2, "0");
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const meridiem = hour24 < 12 ? "a.m" : "p.m";
  return `${offset} • ${hour12}:${minute} ${meridiem}`;
}

const EVENT_TYPE_KEYS = ["class", "pt_session", "facility"];

const defaultEventTypePrefs = () =>
  Object.fromEntries(EVENT_TYPE_KEYS.map((key) => [key, true]));

/** Shared nested shape for each notification category (API coach-settings). */
const defaultNotificationKind = (extras = {}) => ({
  enabled: true,
  ...defaultEventTypePrefs(),
  ...extras,
});

const DEFAULT_NOTIFICATIONS = {
  booking_confirmation: defaultNotificationKind(),
  cancellation_notification: defaultNotificationKind(),
  blackout_cancellation: defaultNotificationKind(),
  attendance_prompt: defaultNotificationKind({ notify_for: "all" }),
  upcoming_event_reminder: defaultNotificationKind({
    reminder_15_minutes: true,
    reminder_1_hour: true,
  }),
};

const NOTIFICATION_OPTIONS = [
  {
    apiKey: "booking_confirmation",
    title: "Booking confirmation",
    description: "Push notification on successful booking.",
    eventTypes: [
      {
        key: "class",
        label: "Classes",
        description: "A member books a class you coach.",
      },
      {
        key: "pt_session",
        label: "Personal training",
        description: "A member books a PT session with you.",
      },
      {
        key: "facility",
        label: "Facility",
        description: "A member books a facility session.",
      },
    ],
  },
  {
    apiKey: "cancellation_notification",
    title: "Cancellation notification",
    description: "Get notified when a booking is canceled.",
    eventTypes: [
      {
        key: "class",
        label: "Classes",
        description: "A class booking is canceled.",
      },
      {
        key: "pt_session",
        label: "Personal training",
        description: "A PT session booking is canceled.",
      },
      {
        key: "facility",
        label: "Facility",
        description: "A facility booking is canceled.",
      },
    ],
  },
  {
    apiKey: "blackout_cancellation",
    title: "Blackout cancellation",
    description: "Notify when a booking is canceled due to a blackout.",
    eventTypes: [
      {
        key: "class",
        label: "Classes",
        description: "A class booking is canceled by a blackout.",
      },
      {
        key: "pt_session",
        label: "Personal training",
        description: "A PT booking is canceled by a blackout.",
      },
      {
        key: "facility",
        label: "Facility",
        description: "A facility booking is canceled by a blackout.",
      },
    ],
  },
  {
    apiKey: "attendance_prompt",
    title: "Attendance",
    description: "Get notified about attendance after a session ends.",
    eventTypes: [
      {
        key: "class",
        label: "Classes",
        description: "Attendance is recorded for a class you coached.",
      },
      {
        key: "pt_session",
        label: "Personal training",
        description: "Attendance is recorded for a PT session with you.",
      },
      {
        key: "facility",
        label: "Facility",
        description: "Attendance is recorded for a facility booking.",
      },
    ],
  },
  {
    apiKey: "upcoming_event_reminder",
    title: "Upcoming event reminder",
    description: "Get a reminder before an event starts.",
    eventTypes: [
      {
        key: "class",
        label: "Classes",
        description: "A class you coach is about to start.",
      },
      {
        key: "pt_session",
        label: "Personal training",
        description: "A PT session with you is about to start.",
      },
      {
        key: "facility",
        label: "Facility",
        description: "A facility booking is about to start.",
      },
    ],
  },
];

const ATTENDANCE_NOTIFY_OPTIONS = [
  { value: "all", label: "All" },
  { value: "attended", label: "Attended only" },
  { value: "missed", label: "Missed only" },
];

const REMINDER_TIMING_OPTIONS = [
  { key: "reminder_15_minutes", label: "15 mins prior" },
  { key: "reminder_1_hour", label: "1 hour prior" },
];

const allEventTypesOn = (kind) =>
  EVENT_TYPE_KEYS.every((key) => Boolean(kind?.[key]));

const reminderTimingsValid = (kind) =>
  !kind?.enabled ||
  Boolean(kind.reminder_15_minutes) ||
  Boolean(kind.reminder_1_hour);

const NOTIFY_FOR_VALUES = ["all", "attended", "missed"];

const deepClone = (value) => JSON.parse(JSON.stringify(value));

/**
 * Expand one API notification field into the nested UI/API shape.
 * Legacy flat true → enabled + all event types on; flat false → enabled off,
 * event-type flags keep defaults (true).
 */
function normalizeNotificationKind(apiKey, value) {
  const base = deepClone(DEFAULT_NOTIFICATIONS[apiKey]);
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const merged = { ...base, ...value };
    if (apiKey === "attendance_prompt") {
      merged.notify_for = NOTIFY_FOR_VALUES.includes(merged.notify_for)
        ? merged.notify_for
        : "all";
    }
    if (apiKey === "upcoming_event_reminder") {
      merged.reminder_15_minutes = Boolean(
        merged.reminder_15_minutes ?? base.reminder_15_minutes
      );
      merged.reminder_1_hour = Boolean(
        merged.reminder_1_hour ?? base.reminder_1_hour
      );
    }
    EVENT_TYPE_KEYS.forEach((key) => {
      merged[key] = Boolean(merged[key]);
    });
    merged.enabled = Boolean(merged.enabled);
    return merged;
  }
  if (typeof value === "boolean") {
    return { ...base, enabled: value };
  }
  return base;
}

/** Map coach-settings notification fields (nested or legacy flat) → local state. */
function notificationsFromApi(data) {
  const next = {};
  NOTIFICATION_OPTIONS.forEach(({ apiKey }) => {
    next[apiKey] = normalizeNotificationKind(apiKey, data[apiKey]);
  });

  // Legacy one_hour_reminder → upcoming_event_reminder.reminder_1_hour only when
  // the nested upcoming object is absent from the API doc.
  const upcomingRaw = data.upcoming_event_reminder;
  const hasUpcomingObject =
    upcomingRaw && typeof upcomingRaw === "object" && !Array.isArray(upcomingRaw);
  if (!hasUpcomingObject && typeof data.one_hour_reminder === "boolean") {
    next.upcoming_event_reminder = {
      ...next.upcoming_event_reminder,
      reminder_1_hour: data.one_hour_reminder,
    };
  }

  return next;
}

/** Nested payload for PUT — always all five kinds, never calendar_invite / one_hour_reminder. */
function notificationsPayloadFromState(notifications) {
  const payload = {};
  NOTIFICATION_OPTIONS.forEach(({ apiKey }) => {
    payload[apiKey] = normalizeNotificationKind(apiKey, notifications[apiKey]);
  });
  return payload;
}

const snapshotOf = (s) => JSON.stringify(s);

const SchedulingSettings = () => {
  const { locals } = useRillContext();
  const theme = locals.activeUser.webAppTheme || "dark";
  const [horizonWeeks, setHorizonWeeks] = React.useState("4");
  const [perTimePeriodEnabled, setPerTimePeriodEnabled] = React.useState(false);
  const [perMemberEnabled, setPerMemberEnabled] = React.useState(false);
  const [perTimePeriodPeriod, setPerTimePeriodPeriod] = React.useState("daily");
  const [perMemberPeriod, setPerMemberPeriod] = React.useState("daily");
  const [perTimePeriodMax, setPerTimePeriodMax] = React.useState(10);
  const [perMemberMax, setPerMemberMax] = React.useState(2);
  const [timezone, setTimezone] = React.useState("America/New_York");
  const [nowTick, setNowTick] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);
  const [notifications, setNotifications] = React.useState(() =>
    deepClone(DEFAULT_NOTIFICATIONS)
  );
  const [reminderTimingError, setReminderTimingError] = React.useState(false);

  const [isLoadingSettings, setIsLoadingSettings] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [toast, setToast] = React.useState(null); // { type: "success"|"error", message: string }
  const toastTimerRef = React.useRef(null);

  // Snapshot of last saved/loaded values for dirty tracking
  const savedSnapshotRef = React.useRef(null);

  const currentSnapshot = snapshotOf({
    horizonWeeks,
    perTimePeriodEnabled,
    perMemberEnabled,
    perTimePeriodPeriod,
    perMemberPeriod,
    perTimePeriodMax,
    perMemberMax,
    timezone,
    notifications,
  });

  const isDirty =
    savedSnapshotRef.current !== null &&
    savedSnapshotRef.current !== currentSnapshot;

  const showToast = React.useCallback((type, message) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ type, message });
    toastTimerRef.current = setTimeout(() => setToast(null), 4000);
  }, []);

  React.useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  const applySettingsFromApi = React.useCallback((data) => {
    if (data.rolling_horizon != null)
      setHorizonWeeks(String(data.rolling_horizon));
    if (typeof data.per_time_period === "boolean")
      setPerTimePeriodEnabled(data.per_time_period);
    if (typeof data.per_member === "boolean")
      setPerMemberEnabled(data.per_member);
    if (data.booking_limit_time_period != null)
      setPerTimePeriodPeriod(data.booking_limit_time_period);
    if (data.per_member_time_period != null)
      setPerMemberPeriod(data.per_member_time_period);
    if (typeof data.max_sessions_per_time_period === "number")
      setPerTimePeriodMax(data.max_sessions_per_time_period);
    if (typeof data.max_sessions_per_member_per_time_period === "number")
      setPerMemberMax(data.max_sessions_per_member_per_time_period);
    if (data.timezone != null) setTimezone(data.timezone);
    setNotifications(notificationsFromApi(data));
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    fetch("/actions/coach-settings", { credentials: "same-origin" })
      .then((res) => {
        if (cancelled) return null;
        if (res.ok && res.status !== 204) return res.json();
        return null;
      })
      .then((data) => {
        if (cancelled) return;
        if (data) applySettingsFromApi(data);
        setIsLoadingSettings(false);
      })
      .catch(() => {
        if (!cancelled) setIsLoadingSettings(false);
      });
    return () => {
      cancelled = true;
    };
  }, [applySettingsFromApi]);

  React.useEffect(() => {
    if (isLoadingSettings) return;
    if (savedSnapshotRef.current !== null) return;
    savedSnapshotRef.current = currentSnapshot;
  }, [isLoadingSettings, currentSnapshot]);

  const buildPayload = React.useCallback(() => {
    return {
      rolling_horizon: Number(horizonWeeks) || 4,
      per_time_period: perTimePeriodEnabled,
      per_member: perMemberEnabled,
      booking_limit_time_period:
        normalizeBookingLimitPeriodForApi(perTimePeriodPeriod),
      per_member_time_period:
        normalizeBookingLimitPeriodForApi(perMemberPeriod),
      max_sessions_per_time_period: perTimePeriodMax,
      max_sessions_per_member_per_time_period: perMemberMax,
      timezone,
      ...notificationsPayloadFromState(notifications),
    };
  }, [
    horizonWeeks,
    perTimePeriodEnabled,
    perMemberEnabled,
    perTimePeriodPeriod,
    perMemberPeriod,
    perTimePeriodMax,
    perMemberMax,
    timezone,
    notifications,
  ]);

  const toggleNotificationMaster = React.useCallback((apiKey) => {
    setNotifications((prev) => {
      const kind = prev[apiKey];
      const nextEnabled = !kind.enabled;
      let next = { ...kind, enabled: nextEnabled };
      if (nextEnabled) {
        const allOff = EVENT_TYPE_KEYS.every((key) => !kind[key]);
        if (allOff) next = { ...next, ...defaultEventTypePrefs() };
      } else {
        EVENT_TYPE_KEYS.forEach((key) => {
          next[key] = false;
        });
      }
      return { ...prev, [apiKey]: next };
    });
    if (apiKey === "upcoming_event_reminder") setReminderTimingError(false);
  }, []);

  const toggleNotificationEventType = React.useCallback((apiKey, eventKey) => {
    setNotifications((prev) => {
      const kind = prev[apiKey];
      if (!kind.enabled) return prev;
      
      const nextKind = { ...kind, [eventKey]: !kind[eventKey] };
      const allOff = EVENT_TYPE_KEYS.every((key) => !nextKind[key]);
      
      if (allOff) {
        nextKind.enabled = false;
      }
      
      return {
        ...prev,
        [apiKey]: nextKind,
      };
    });
  }, []);

  const setAttendanceNotifyFor = React.useCallback((value) => {
    setNotifications((prev) => ({
      ...prev,
      attendance_prompt: {
        ...prev.attendance_prompt,
        notify_for: value,
      },
    }));
  }, []);

  const toggleReminderTiming = React.useCallback(
    (timingKey) => {
      const kind = notifications.upcoming_event_reminder;
      if (!kind.enabled) return;
      const nextVal = !kind[timingKey];
      if (!nextVal) {
        const otherKey =
          timingKey === "reminder_15_minutes"
            ? "reminder_1_hour"
            : "reminder_15_minutes";
        if (!kind[otherKey]) {
          setReminderTimingError(true);
          return;
        }
      }
      setReminderTimingError(false);
      setNotifications((prev) => ({
        ...prev,
        upcoming_event_reminder: {
          ...prev.upcoming_event_reminder,
          [timingKey]: nextVal,
        },
      }));
    },
    [notifications.upcoming_event_reminder]
  );

  const handleSave = React.useCallback(() => {
    if (!reminderTimingsValid(notifications.upcoming_event_reminder)) {
      setReminderTimingError(true);
      return;
    }
    setReminderTimingError(false);
    setSaving(true);
    const payload = buildPayload();
    const snap = currentSnapshot;
    fetch("/actions/coach-settings", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(payload),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        savedSnapshotRef.current = snap;
        showToast("success", "Settings saved successfully.");
      })
      .catch(() => {
        showToast("error", "Failed to save settings. Please try again.");
      })
      .finally(() => {
        setSaving(false);
      });
  }, [buildPayload, currentSnapshot, notifications.upcoming_event_reminder, showToast]);

  const handleDiscard = React.useCallback(() => {
    if (!savedSnapshotRef.current) return;
    const s = JSON.parse(savedSnapshotRef.current);
    setHorizonWeeks(s.horizonWeeks);
    setPerTimePeriodEnabled(s.perTimePeriodEnabled);
    setPerMemberEnabled(s.perMemberEnabled);
    setPerTimePeriodPeriod(s.perTimePeriodPeriod);
    setPerMemberPeriod(s.perMemberPeriod);
    setPerTimePeriodMax(s.perTimePeriodMax);
    setPerMemberMax(s.perMemberMax);
    setTimezone(s.timezone);
    setNotifications(deepClone(s.notifications));
    setReminderTimingError(false);
  }, []);

  if (isLoadingSettings) {
    return (
      <div className="scheduling-settings">
        <Toast
          open={Boolean(toast)}
          type={toast?.type ?? "success"}
          message={toast?.message ?? ""}
        />
        <h1 className="scheduling-settings-page-title">Settings</h1>
        <SettingsSkeleton theme={theme} />
      </div>
    );
  }

  return (
    <div className="scheduling-settings">
      <Toast
        open={Boolean(toast)}
        type={toast?.type ?? "success"}
        message={toast?.message ?? ""}
      />

      <div className="scheduling-settings-page-header">
        <h1 className="scheduling-settings-page-title">Settings</h1>
      </div>

      <section className="scheduling-settings-card scheduling-settings-card-cols">
        <div className="scheduling-settings-card-head">
          <h2 className="scheduling-settings-title">Rolling Horizon</h2>
          <p className="scheduling-settings-desc">
            Control how far in advance members can make bookings.
          </p>
        </div>
        <div className="scheduling-settings-card-body">
          <div className="scheduling-settings-row scheduling-settings-row-select">
            <select
              className="scheduling-settings-select"
              value={horizonWeeks}
              onChange={(e) => setHorizonWeeks(e.target.value)}
              aria-label="Rolling horizon"
            >
              {HORIZON_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="scheduling-settings-info">
            <img
              src="/img/new-icons/vuesax-linear-info-circle.svg"
              alt=""
              className="scheduling-settings-info-icon"
            />
            <span>
              Maximum horizon is 12 weeks, members can not book beyond this
              window
            </span>
          </div>
        </div>
      </section>

      <section className="scheduling-settings-card scheduling-settings-card-cols scheduling-booking-limits-card">
        <div className="scheduling-settings-card-head">
          <h2 className="scheduling-settings-title">Booking Limits</h2>
          <p className="scheduling-settings-desc">
            Limit how often members can book sessions.
          </p>
        </div>
        <div className="scheduling-settings-card-body">
          <div className="scheduling-booking-limits-blocks">
            <div className="scheduling-booking-limit-block">
              <div className="scheduling-access-row scheduling-access-row-switch">
                <input
                  type="checkbox"
                  role="switch"
                  aria-label="Per time period"
                  aria-checked={perTimePeriodEnabled}
                  checked={perTimePeriodEnabled}
                  onChange={() =>
                    setPerTimePeriodEnabled(!perTimePeriodEnabled)
                  }
                />
                <span className="scheduling-access-row-text">
                  <span className="scheduling-access-row-title">
                    Per Time Period
                  </span>
                  <span className="scheduling-access-row-desc">
                    Limit how many bookings can be made within a selected time
                    period for a session.
                  </span>
                </span>
              </div>
              {perTimePeriodEnabled && (
                <div className="scheduling-booking-limit-cols">
                  <div className="scheduling-booking-limit-col">
                    <span className="scheduling-booking-limit-label">
                      Select time period
                    </span>
                    <div
                      className="scheduling-booking-limit-radios"
                      role="radiogroup"
                      aria-label="Time period"
                    >
                      {TIME_PERIOD_OPTIONS.map(({ id, label }) => (
                        <label key={id} className="radio-input">
                          <input
                            type="radio"
                            name="booking-limit-time-period"
                            value={id}
                            checked={perTimePeriodPeriod === id}
                            onChange={() => setPerTimePeriodPeriod(id)}
                          />
                          <span className="radio" aria-hidden="true" />
                          <span className="text">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="scheduling-booking-limit-col scheduling-booking-limit-max">
                    <span className="scheduling-booking-limit-label">
                      Max sessions per{" "}
                      {perTimePeriodPeriod === "daily"
                        ? "day"
                        : perTimePeriodPeriod === "weekly"
                          ? "week"
                          : "month"}
                    </span>
                    <select
                      className="scheduling-booking-limit-select"
                      value={perTimePeriodMax}
                      onChange={(e) =>
                        setPerTimePeriodMax(Number(e.target.value))
                      }
                      aria-label="Max sessions per time period"
                    >
                      {MAX_SESSIONS_OPTIONS.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>

            <div className="scheduling-booking-limit-block">
              <div className="scheduling-access-row scheduling-access-row-switch">
                <input
                  type="checkbox"
                  role="switch"
                  aria-label="Per member"
                  aria-checked={perMemberEnabled}
                  checked={perMemberEnabled}
                  onChange={() => setPerMemberEnabled(!perMemberEnabled)}
                />
                <span className="scheduling-access-row-text">
                  <span className="scheduling-access-row-title">
                    Per Member
                  </span>
                  <span className="scheduling-access-row-desc">
                    Set individual booking limits for each member across all
                    session types.
                  </span>
                </span>
              </div>
              {perMemberEnabled && (
                <div className="scheduling-booking-limit-cols">
                  <div className="scheduling-booking-limit-col">
                    <span className="scheduling-booking-limit-label">
                      Select time period
                    </span>
                    <div
                      className="scheduling-booking-limit-radios"
                      role="radiogroup"
                      aria-label="Per member time period"
                    >
                      {TIME_PERIOD_OPTIONS.map(({ id, label }) => (
                        <label key={id} className="radio-input">
                          <input
                            type="radio"
                            name="booking-limit-per-member-period"
                            value={id}
                            checked={perMemberPeriod === id}
                            onChange={() => setPerMemberPeriod(id)}
                          />
                          <span className="radio" aria-hidden="true" />
                          <span className="text">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="scheduling-booking-limit-col scheduling-booking-limit-max">
                    <span className="scheduling-booking-limit-label">
                      Max sessions per member per{" "}
                      {perMemberPeriod === "daily"
                        ? "day"
                        : perMemberPeriod === "weekly"
                          ? "week"
                          : "month"}
                    </span>
                    <select
                      className="scheduling-booking-limit-select"
                      value={perMemberMax}
                      onChange={(e) => setPerMemberMax(Number(e.target.value))}
                      aria-label="Max sessions per member per time period"
                    >
                      {MAX_SESSIONS_OPTIONS.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="scheduling-settings-card scheduling-settings-card-cols">
        <div className="scheduling-settings-card-head">
          <h2 className="scheduling-settings-title">Timezone</h2>
          <p className="scheduling-settings-desc">
            All scheduling, notifications, and calendar invites use this
            timezone.
          </p>
        </div>
        <div className="scheduling-settings-card-body">
          <div className="scheduling-settings-row scheduling-settings-row-select">
            <select
              className="scheduling-settings-select scheduling-timezone-select"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              aria-label="Timezone"
            >
              {TIMEZONE_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>
                  {`${label} - ${formatZoneOffsetAndTime(value, nowTick)}`}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <section className="scheduling-settings-card scheduling-settings-card-cols">
        <div className="scheduling-settings-card-head">
          <h2 className="scheduling-settings-title">Notifications</h2>
          <p className="scheduling-settings-desc">
            Choose which scheduling alerts are pushed to your phone.
          </p>
          <div className="scheduling-notif-app-callout">
            <span className="scheduling-notif-app-callout-icon" aria-hidden="true">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                width="20"
                height="20"
              >
                <rect
                  x="7"
                  y="2.5"
                  width="10"
                  height="19"
                  rx="2.5"
                  stroke="currentColor"
                  strokeWidth="1.75"
                />
                <path
                  d="M10 5.25h4"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <div className="scheduling-notif-app-callout-body">
              <p className="scheduling-notif-app-callout-text">
                Push only, sent to the <strong>MSB Coach app</strong>. Install
                it and allow notifications to receive these.
              </p>
              <div className="scheduling-notif-app-callout-actions">
                <a
                  href={COACH_APP_APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="scheduling-notif-app-callout-btn"
                >
                  iOS
                </a>
                <a
                  href={COACH_APP_PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="scheduling-notif-app-callout-btn"
                >
                  Android
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="scheduling-settings-card-body scheduling-notifications-options">
          {NOTIFICATION_OPTIONS.map(
            ({ apiKey, title, description, eventTypes }) => {
              const kind = notifications[apiKey];
              const enabled = Boolean(kind?.enabled);
              const activeCount = eventTypes.reduce((count, { key }) => count + (kind?.[key] ? 1 : 0), 0);
              const totalCount = eventTypes.length;
              return (
                <div key={apiKey} className="scheduling-notif-category">
                  <div className="scheduling-notif-category-header">
                    <div className="scheduling-access-row scheduling-access-row-switch">
                      <input
                        type="checkbox"
                        role="switch"
                        aria-label={title}
                        aria-checked={enabled}
                        checked={enabled}
                        onChange={() => toggleNotificationMaster(apiKey)}
                      />
                      <span className="scheduling-access-row-text">
                        <span className="scheduling-access-row-title">
                          {title}
                        </span>
                        <span className="scheduling-access-row-desc">
                          {description}
                        </span>
                      </span>
                    </div>
                    {enabled ? (
                      activeCount === totalCount ? (
                        <span className="scheduling-notif-all-types">All event types</span>
                      ) : (
                        <span className="scheduling-notif-all-types">{activeCount} of {totalCount} on</span>
                      )
                    ) : (
                      <span className="scheduling-notif-all-types">Off</span>
                    )}
                  </div>

                  <div
                    className={`scheduling-notif-nest${
                      enabled ? "" : " is-disabled"
                    }`}
                  >
                    <div className="scheduling-notif-event-types">
                      {eventTypes.map(({ key, label, description: typeDesc }) => {
                        const checked = Boolean(kind?.[key]);
                        return (
                          <div
                            key={key}
                            className="scheduling-access-row scheduling-access-row-switch scheduling-notif-child-row"
                          >
                            <input
                              type="checkbox"
                              role="switch"
                              aria-label={`${title}: ${label}`}
                              aria-checked={checked}
                              checked={checked}
                              disabled={!enabled}
                              onChange={() =>
                                toggleNotificationEventType(apiKey, key)
                              }
                            />
                            <span className="scheduling-access-row-text">
                              <span className="scheduling-access-row-title">
                                {label}
                              </span>
                              <span className="scheduling-access-row-desc">
                                {typeDesc}
                              </span>
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {apiKey === "attendance_prompt" && (
                      <div className="scheduling-notif-choice-row">
                        <span className="scheduling-notif-sublabel">
                          Notify me for
                        </span>
                        <div
                          className="scheduling-notif-segmented"
                          role="radiogroup"
                          aria-label="Notify me for"
                        >
                          {ATTENDANCE_NOTIFY_OPTIONS.map(({ value, label }) => {
                            const selected = kind.notify_for === value;
                            return (
                              <button
                                key={value}
                                type="button"
                                role="radio"
                                aria-checked={selected}
                                className={`scheduling-notif-segment${
                                  selected ? " is-selected" : ""
                                }`}
                                disabled={!enabled}
                                onClick={() => setAttendanceNotifyFor(value)}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {apiKey === "upcoming_event_reminder" && (
                      <div className="scheduling-notif-choice-row">
                        <span className="scheduling-notif-sublabel">
                          Remind me
                        </span>
                        <div
                          className="scheduling-notif-chip-group"
                          role="group"
                          aria-label="Remind me"
                        >
                          {REMINDER_TIMING_OPTIONS.map(({ key, label }) => {
                            const selected = Boolean(kind?.[key]);
                            return (
                              <button
                                key={key}
                                type="button"
                                aria-pressed={selected}
                                className={`scheduling-notif-chip${
                                  selected ? " is-selected" : ""
                                }`}
                                disabled={!enabled}
                                onClick={() => toggleReminderTiming(key)}
                              >
                                {selected && (
                                  <span
                                    className="scheduling-notif-chip-check"
                                    aria-hidden="true"
                                  >
                                    ✓
                                  </span>
                                )}
                                {label}
                              </button>
                            );
                          })}
                        </div>
                        <span className="scheduling-notif-choice-hint">
                          Select one or both
                        </span>
                        {reminderTimingError && (
                          <p className="scheduling-notif-error" role="alert">
                            Select at least one reminder time.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            }
          )}
        </div>
      </section>

      {isDirty && (
        <footer className="scheduling-settings-footer">
          <FloatingButtons
            failedText="Discard"
            successText={saving ? "Saving…" : "Save changes"}
            successDisabled={saving}
            handleFailed={handleDiscard}
            handleSuccess={handleSave}
            label="You have unsaved changes"
          />
        </footer>
      )}
    </div>
  );
};

export default SchedulingSettings;
